import React, { useState } from "react";
import { Search, SlidersHorizontal, LayoutGrid, List } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "../ui/sheet";

// v14ad Fase 0 — Uniforme control-row voor overzichten (Klanten, Projecten,
// Offertes, Facturen). 1 component, alle slots, Design System v1 strikt:
//   input/select/button hoogte 11 (44px), rounded-xl, bg-white, border-[#E5E2DC]
// Desktop: alles op één rij.
// Mobile: search + primary actions op eigen rij, filters/sort verzameld in
// een Sheet-drawer achter een compacte "Filter & sorteer" knop.

const CONTROL_H = "h-11 rounded-xl";
const CONTROL_BORDER = "bg-white border-[#E5E2DC]";

/** @param {{ options: {value: string, label: string}[], value: string, onChange: (v:string)=>void, placeholder?: string, testId?: string, widthClass?: string }} p */
function ControlSelect({ options, value, onChange, placeholder, testId, widthClass = "w-full sm:w-44" }) {
  return (
    <Select value={value || "__unset__"} onValueChange={(v) => onChange(v === "__unset__" ? "" : v)}>
      <SelectTrigger className={`${CONTROL_H} ${widthClass}`} data-testid={testId}>
        <SelectValue placeholder={placeholder || "Kies…"} />
      </SelectTrigger>
      <SelectContent>
        {placeholder && <SelectItem value="__unset__">{placeholder}</SelectItem>}
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-testid={testId ? `${testId}-opt-${o.value}` : undefined}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function ViewToggle({ value, onChange, testId }) {
  const btnBase = `h-9 w-9 rounded-lg inline-flex items-center justify-center transition-colors`;
  const active = "bg-white text-[#2C2C2A] shadow-sm";
  const idle = "text-[#6E6E6C] hover:text-[#2C2C2A]";
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-[#F0EFEA] border border-[#E5E2DC]" data-testid={testId}>
      <button
        type="button"
        onClick={() => onChange("list")}
        className={`${btnBase} ${value === "list" ? active : idle}`}
        aria-label="Lijstweergave"
        title="Lijst"
        data-testid={`${testId}-list`}
      >
        <List className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange("cards")}
        className={`${btnBase} ${value === "cards" ? active : idle}`}
        aria-label="Kaartenweergave"
        title="Kaarten"
        data-testid={`${testId}-cards`}
      >
        <LayoutGrid className="w-4 h-4" />
      </button>
    </div>
  );
}

/**
 * @param {{
 *  moduleKey: string,
 *  search?: { value: string, onChange: (v:string)=>void, placeholder?: string, testId?: string },
 *  filters?: Array<{ id: string, placeholder: string, value: string, onChange: (v:string)=>void, options: {value:string,label:string}[], testId?: string }>,
 *  sort?: { value: string, onChange: (v:string)=>void, options: {value:string,label:string}[], testId?: string },
 *  view?: { value: string, onChange: (v:string)=>void, testId?: string, showOnMobile?: boolean },
 *  actions?: React.ReactNode,
 * }} p
 */
export default function OverviewControls({ moduleKey, search, filters = [], sort, view, actions }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const hasFilters = filters.length > 0;
  const hasSort = !!sort && Array.isArray(sort.options) && sort.options.length > 0;
  const hasSheet = hasFilters || hasSort;

  return (
    <div className="space-y-3" data-testid={`overview-controls-${moduleKey}`}>
      {/* Desktop */}
      <div className="hidden md:flex md:items-center md:gap-3 md:flex-wrap">
        {search && (
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4F4F4D]" />
            <Input
              value={search.value}
              onChange={(e) => search.onChange(e.target.value)}
              placeholder={search.placeholder || "Zoeken…"}
              className={`${CONTROL_H} ${CONTROL_BORDER} pl-9`}
              data-testid={search.testId || `${moduleKey}-search`}
            />
          </div>
        )}
        {filters.map((f) => (
          <ControlSelect
            key={f.id}
            options={f.options}
            value={f.value}
            onChange={f.onChange}
            placeholder={f.placeholder}
            testId={f.testId || `${moduleKey}-filter-${f.id}`}
          />
        ))}
        {hasSort && (
          <ControlSelect
            options={sort.options}
            value={sort.value}
            onChange={sort.onChange}
            testId={sort.testId || `${moduleKey}-sort`}
            widthClass="w-full sm:w-56"
          />
        )}
        <div className="flex-1" />
        {view && <ViewToggle value={view.value} onChange={view.onChange} testId={view.testId || `${moduleKey}-view`} />}
        {actions}
      </div>

      {/* Mobile */}
      <div className="md:hidden space-y-2">
        <div className="flex gap-2">
          {search && (
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4F4F4D]" />
              <Input
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                placeholder={search.placeholder || "Zoeken…"}
                className={`${CONTROL_H} ${CONTROL_BORDER} pl-9`}
                data-testid={`${moduleKey}-search-mobile`}
              />
            </div>
          )}
          {hasSheet && (
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className={`${CONTROL_H} ${CONTROL_BORDER} px-3 shrink-0`}
                  data-testid={`${moduleKey}-filter-sort-mobile-btn`}
                  aria-label="Filter & sorteer"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="rounded-t-2xl max-h-[85dvh] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="text-left font-heading text-xl text-[#2C2C2A]">Filter & sorteer</SheetTitle>
                </SheetHeader>
                <div className="space-y-4 py-4">
                  {filters.map((f) => (
                    <div key={f.id} className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-[#6E6E6C]">{f.placeholder}</Label>
                      <ControlSelect
                        options={f.options}
                        value={f.value}
                        onChange={f.onChange}
                        placeholder={f.placeholder}
                        testId={`${moduleKey}-filter-${f.id}-mobile`}
                        widthClass="w-full"
                      />
                    </div>
                  ))}
                  {hasSort && (
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-[#6E6E6C]">Sorteren</Label>
                      <ControlSelect
                        options={sort.options}
                        value={sort.value}
                        onChange={sort.onChange}
                        testId={`${moduleKey}-sort-mobile`}
                        widthClass="w-full"
                      />
                    </div>
                  )}
                  {view && view.showOnMobile !== false && (
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-wide text-[#6E6E6C]">Weergave</Label>
                      <ViewToggle value={view.value} onChange={view.onChange} testId={`${moduleKey}-view-mobile`} />
                    </div>
                  )}
                </div>
                <SheetFooter>
                  <Button
                    onClick={() => setMobileOpen(false)}
                    className={`${CONTROL_H} w-full bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white`}
                    data-testid={`${moduleKey}-filter-sort-mobile-apply`}
                  >
                    Toepassen
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          )}
        </div>
        {actions && <div className="flex">{actions}</div>}
      </div>
    </div>
  );
}
